import { Formik } from 'formik';
import { Alert, Card, Collapse, Space, Spin, Typography } from 'antd';
import {
    UserOutlined,
    CalendarOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    ExperimentOutlined,
    HeartOutlined,
    CaretRightOutlined
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import { openPrintPreview } from '../../components/Print/PrintPreview';
import { calculateAge } from '../../utils/functions';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { Breadcrumb } from '../../components/Card/Cards';
import { useDischargeForm } from './hooks/useDischargeForm';
import { FormHeader } from './components/FormHeader';
import { TextField, TextAreaField, DateField, TimeField, SelectField } from './components/FormFields';
import { FormActionButtons } from './components/FormActionButtons';
import { CustomTable } from '../../components/Table';
import PatientCCPaperView from '../../components/PatientCCPaperView/PatientCCPaperView';
import { useSelector } from 'react-redux';

const { Title } = Typography;
const { Panel } = Collapse;

/**
 * Main Discharge Form Component
 * Simplified version with direct Collapse/Panel usage
 */
const DischargeForm = () => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    const roomName = searchParams.get('roomName');
    const roomType = searchParams.get('roomType');
    // Use custom hook for ID extraction


    // Use custom hook for all form logic
    const {
        loading,
        error,
        isEditMode,
        initialValues,
        servicesTableData,
        toggleEditMode,
        handleFormSubmit,
    } = useDischargeForm();

    // Breadcrumb configuration
    const BreadcrumbList = [
        {
            name: 'Discharge Form',
            linkTo: '',
        },
    ];

    const { CcPaperDetail } = useSelector(state => state?.patient);

    // Handle print functionality
    const handlePrint = (values) => {
        const patientInfo = CcPaperDetail || {};

        const formatDate = (date) => {
            if (!date) return '—';
            try {
                // If it's a dayjs object (common in Formik state)
                if (date.format && typeof date.format === 'function') {
                    return date.format('DD MMM YYYY');
                }
                // If it's already a string in correct format or ISO
                return moment(date).format('DD MMM YYYY');
            } catch (e) {
                return typeof date === 'string' ? date : '—';
            }
        };

        const formatTime = (time) => {
            if (!time) return '';
            try {
                // If it's a dayjs object (which it usually is in Formik state)
                if (time.format) return time.format('h:mm A');
                // If it's a string
                return moment(time, 'h:mm a').format('h:mm A');
            } catch (e) {
                return typeof time === 'string' ? time : '';
            }
        };

        const sections = [
            {
                title: 'Patient Information',
                fields: [
                    { label: 'UHID', value: patientInfo.registrationNo || '—' },
                    { label: 'Name', value: values.customerName || patientInfo.name || '—' },
                    // { label: 'Treatment Plan', value: values.treatmentName || '—' },
                    { label: 'Age / Gender', value: `${patientInfo.birthDate ? calculateAge(patientInfo.birthDate) : values.age || '—'} / ${values.gender || '—'}` },
                    { label: 'Phone No.', value: patientInfo.phoneNo || '—' },
                    { label: 'Email', value: values.email || '—' },
                    { label: 'Room Name', value: roomName || '—' },
                    { label: 'Room Type', value: roomType || '—' },
                ],
            },
            {
                title: 'Admission Summary',
                fields: [
                    {
                        label: 'Admission Date',
                        value: `${formatDate(values.startDate)} ${values.admissionTime ? `at ${formatTime(values.admissionTime)}` : ''}`
                    },
                    {
                        label: 'Discharge Date',
                        value: `${formatDate(values.endDate)} ${values.dischargeTime ? `at ${formatTime(values.dischargeTime)}` : ''}`
                    },
                    { label: 'Total Stay', value: `${values.totalDays || '0'} Days` },
                    { label: 'Discharge Type', value: values.dischargeType || '—' },
                    { label: 'Diagnosis', value: values.diagnosis || '—' },
                ],
            },
            {
                title: 'Clinical Findings',
                fields: [
                    { label: 'Hetu (Cause)', value: values.hetu || '—' },
                    { label: 'Addiction', value: values.addiction || '—' },
                    { label: 'Previous History', value: values.previousHistory || '—' },
                ],
            },
            {
                title: 'Complaints',
                fields: [
                    { label: 'Admission Complaints', value: values.admissionComplaints || '—' },
                    { label: 'Discharge Complaints', value: values.dischargeComplaints || '—' },
                ],
            },
            {
                title: 'Investigations',
                fields: [
                    { label: 'Before Admission', value: values.investigationBefore || '—' },
                    { label: 'During Admission', value: values.investigationAdmit || '—' },
                ],
            },
        ];

        // Add Treatment Table
        if (servicesTableData && servicesTableData.length > 0) {
            sections.push({
                title: `Treatment Given ${values.totalDays ? `(${values.totalDays} Days)` : ''}`,
                type: 'table',
                headers: ['Sr.', 'Name of Treatment', 'Timing', 'Days', 'Medicines'],
                data: servicesTableData.map((item, index) => [
                    index + 1,
                    item.serviceName,
                    item.time,
                    item.days,
                    item.medicine
                ])
            });
        }

        // Add Discharge Advice
        sections.push({
            title: 'Discharge Advice',
            fields: [
                { label: 'Medicine Advised', value: values.medicineAdvised || '—' },
                { label: 'Investigations Advised', value: values.investigationsAdvised || '—' },
                { label: 'Virechana Details', value: values.virechana || '—' },
                { label: 'Diet Advice', value: values.diet || '—' },
                { label: 'Exercise Advice', value: values.Exercise || '—' },
                { label: 'Next Panchakarma Advice', value: values.nextPanchakramaAdvise || '—' },
                { label: 'Mental Peace Guidance', value: values.instructionMentalPeace || '—' },
            ],
        });

        openPrintPreview(values, 'DISCHARGE SUMMARY', { sections });
    };



    // Table columns with improved formatting
    const columns = [
        {
            title: 'Sr.',
            key: 'index',
            width: '3%',
            render: (text, record, index) => (
                <span style={{ color: '#8c8c8c' }}>{index + 1}</span>
            ),
        },
        {
            title: 'Name Of Treatment',
            dataIndex: 'serviceName',
            key: 'serviceName',
            width: '35%',
            render: (text) => (
                <span style={{ fontWeight: '600', color: '#434343' }}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Timing',
            dataIndex: 'time',
            key: 'time',
            width: '15%',
            render: (text) => (
                <span style={{ color: '#595959', fontSize: '13px' }}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Days',
            dataIndex: 'days',
            key: 'days',
            width: '10%',
            render: (text) => (
                <span style={{ color: '#595959', fontWeight: '500' }}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Medicines Recorded',
            dataIndex: 'medicine',
            key: 'medicine',
            width: '40%',
            render: (text) => {
                if (text === '—' || !text) {
                    return <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>—</span>;
                }

                // If text contains [Note: ...], split it out to style separately
                const noteMatch = text.match(/\[Note: (.*?)\]/);
                const mainText = noteMatch ? text.replace(noteMatch[0], '').trim() : text;
                const noteText = noteMatch ? noteMatch[1] : null;

                const medicineList = typeof mainText === 'string'
                    ? mainText.split(',').map(m => m.trim()).filter(m => m)
                    : [];

                return (
                    <div style={{ color: '#595959', lineHeight: '1.4' }}>
                        {medicineList.map((med, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'start', gap: '6px' }}>
                                <span style={{ color: '#bfbfbf', fontSize: '10px', marginTop: '4px' }}>•</span>
                                <span>{med}</span>
                            </div>
                        ))}
                        {noteText && (
                            <div style={{
                                marginTop: '4px',
                                fontSize: '11px',
                                color: '#d46b08',
                                background: '#fff7e6',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                border: '1px solid #ffd591'
                            }}>
                                <strong>Note:</strong> {noteText}
                            </div>
                        )}
                        {!medicineList.length && !noteText && <span>—</span>}
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <Header />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    <Breadcrumb page={BreadcrumbList} />

                    <Card
                        style={{
                            // maxWidth: '1400px',
                            margin: '0 auto',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderRadius: '8px',
                        }}
                    >
                        {/* Loading State */}
                        {loading && (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spin size="large" tip="Loading discharge form data..." />
                            </div>
                        )}

                        {/* Error State */}
                        {error && !loading && (
                            <Alert
                                message="Error Loading Data"
                                description={error?.message || 'Failed to load discharge form data. Please try again.'}
                                type="error"
                                showIcon
                                style={{ marginBottom: '20px' }}
                            />
                        )}

                        {/* Form Content */}
                        {!loading && !error && (
                            <>
                                <FormHeader />

                                <Formik
                                    enableReinitialize={true}
                                    initialValues={initialValues}
                                    onSubmit={handleFormSubmit}
                                >
                                    {({ values, handleBlur, handleChange, touched, handleSubmit, isSubmitting, dirty, setFieldValue }) => {
                                        return (
                                            <form onSubmit={handleSubmit}>
                                                <Collapse
                                                    defaultActiveKey={['1', '2', '3', '4', '5', '6']}
                                                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                                                    style={{ background: '#fafafa', border: 'none' }}
                                                    expandIconPosition="end"
                                                >
                                                    {/* Patient Information Section */}
                                                    <Panel
                                                        header={
                                                            <Space>
                                                                <UserOutlined style={{ fontSize: '18px', color: '#595959' }} />
                                                                <Title level={5} style={{ margin: 0 }}>Patient Information</Title>
                                                            </Space>
                                                        }
                                                        key="1"
                                                        style={{ marginBottom: '16px', background: 'white', borderRadius: '8px' }}
                                                    >
                                                        <PatientCCPaperView userId={initialValues?.customerId} extraInfo={{ "Room Name": roomName, "Room Type": roomType }} />

                                                    </Panel>

                                                    {/* Admission Details Section */}
                                                    <Panel
                                                        header={
                                                            <Space>
                                                                <CalendarOutlined style={{ fontSize: '18px', color: '#595959' }} />
                                                                <Title level={5} style={{ margin: 0 }}>Admission Details</Title>
                                                            </Space>
                                                        }
                                                        key="2"
                                                        style={{ marginBottom: '16px', background: 'white', borderRadius: '8px' }}
                                                    >
                                                        <div className="row" style={{ padding: '8px 0' }}>
                                                            <TextField
                                                                label="Treatment Plan"
                                                                name="treatmentName"
                                                                value={values?.treatmentName}
                                                                placeholder="Treatment Name"
                                                                disabled={true}
                                                                colClass="col-xl-8 col-md-8 col-12"
                                                            />

                                                            <SelectField
                                                                label="Discharge Type"
                                                                name="dischargeType"
                                                                value={values?.dischargeType}
                                                                onChange={(val) => handleChange({ target: { name: 'dischargeType', value: val } })}
                                                                onBlur={handleBlur}
                                                                placeholder="Select Discharge Type"
                                                                disabled={!isEditMode}
                                                                options={[
                                                                    { label: 'Treatment Completed', value: 'Planned' },
                                                                    { label: 'Relieved / Satisfied', value: 'Relieved' },
                                                                    { label: 'Left Against Advice (LAA)', value: 'LAMA' },
                                                                    { label: 'Departure Against Advice', value: 'DAMA' },
                                                                    { label: 'Referred to Other Facility', value: 'Referred' },
                                                                    { label: 'Transferred to Other Center', value: 'Transferred' },
                                                                    { label: 'Stay Shortened', value: 'Absconded' },
                                                                    { label: 'Treatment Completed / Request Departure', value: 'Medically Fit' },
                                                                ]}
                                                                colClass="col-xl-4 col-md-4 col-12"
                                                            />

                                                            <DateField
                                                                label="Admission Date (DD/MM/YYYY)"
                                                                name="startDate"
                                                                value={values?.startDate}
                                                                onChange={(val) => {
                                                                    setFieldValue('startDate', val);
                                                                    // Recalculate total days if both dates exist
                                                                    if (val && values.endDate) {
                                                                        const start = dayjs(val);
                                                                        const end = dayjs(values.endDate);
                                                                        if (start.isValid() && end.isValid()) {
                                                                            setFieldValue('totalDays', end.diff(start, 'day'));
                                                                        }
                                                                    }
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Start Date"
                                                                disabled={!isEditMode}
                                                            />

                                                            <DateField
                                                                label="Discharge Date (DD/MM/YYYY)"
                                                                name="endDate"
                                                                value={values?.endDate}
                                                                onChange={(val) => {
                                                                    setFieldValue('endDate', val);
                                                                    // Recalculate total days if both dates exist
                                                                    if (values.startDate && val) {
                                                                        const start = dayjs(values.startDate);
                                                                        const end = dayjs(val);
                                                                        if (start.isValid() && end.isValid()) {
                                                                            setFieldValue('totalDays', end.diff(start, 'day'));
                                                                        }
                                                                    }
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="End Date"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextField
                                                                label="Total Days"
                                                                name="totalDays"
                                                                value={values?.totalDays}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                placeholder="Total Days"
                                                                disabled={true}
                                                            />

                                                            <TimeField
                                                                label="Admission Time"
                                                                name="admissionTime"
                                                                value={values?.admissionTime}
                                                                onChange={(time) => handleChange({ target: { name: 'admissionTime', value: time } })}
                                                                onBlur={handleBlur}
                                                                placeholder="Select Time"
                                                                disabled={!isEditMode || values?.admissionTime}
                                                            />

                                                            <TimeField
                                                                label="Discharge Time"
                                                                name="dischargeTime"
                                                                value={values?.dischargeTime}
                                                                onChange={(time) => handleChange({ target: { name: 'dischargeTime', value: time } })}
                                                                onBlur={handleBlur}
                                                                placeholder="Select Time"
                                                                disabled={!isEditMode || values?.dischargeTime}
                                                            />
                                                        </div>
                                                    </Panel>

                                                    {/* Medical History Section */}
                                                    <Panel
                                                        header={
                                                            <Space>
                                                                <FileTextOutlined style={{ fontSize: '18px', color: '#595959' }} />
                                                                <Title level={5} style={{ margin: 0 }}>Medical History & Diagnosis</Title>
                                                            </Space>
                                                        }
                                                        key="3"
                                                        style={{ marginBottom: '16px', background: 'white', borderRadius: '8px' }}
                                                    >
                                                        <div className="row" style={{ padding: '8px 0' }}>
                                                            <TextField
                                                                label="Diagnosis"
                                                                name="diagnosis"
                                                                value={values?.diagnosis}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Diagnosis"
                                                                disabled={!isEditMode}
                                                                colClass="col-xl-6 col-md-6 col-12"
                                                            />

                                                            <TextAreaField
                                                                label="Hetu (Cause)"
                                                                name="hetu"
                                                                value={values?.hetu}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'hetu', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Hetu"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Addiction"
                                                                name="addiction"
                                                                value={values?.addiction}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'addiction', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Addiction Details"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Previous History"
                                                                name="previousHistory"
                                                                value={values?.previousHistory}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'previousHistory', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Previous History"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Chief Complaints at Arrival"
                                                                name="admissionComplaints"
                                                                value={values?.admissionComplaints}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'admissionComplaints', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Complaints at Arrival"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Discharge Complaints"
                                                                name="dischargeComplaints"
                                                                value={values?.dischargeComplaints}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'dischargeComplaints', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Status at Departure"
                                                                disabled={!isEditMode}
                                                            />
                                                        </div>
                                                    </Panel>

                                                    {/* Investigations Section */}
                                                    <Panel
                                                        header={
                                                            <Space>
                                                                <ExperimentOutlined style={{ fontSize: '18px', color: '#595959' }} />
                                                                <Title level={5} style={{ margin: 0 }}>Investigations</Title>
                                                            </Space>
                                                        }
                                                        key="4"
                                                        style={{ marginBottom: '16px', background: 'white', borderRadius: '8px' }}
                                                    >
                                                        <div className="row" style={{ padding: '8px 0' }}>
                                                            <TextAreaField
                                                                label="Investigation Before Admission"
                                                                name="investigationBefore"
                                                                value={values?.investigationBefore}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'investigationBefore', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Investigation Details Before Admission"
                                                                disabled={!isEditMode}
                                                                rows={4}
                                                            />

                                                            <TextAreaField
                                                                label="Investigation During Admission"
                                                                name="investigationAdmit"
                                                                value={values?.investigationAdmit}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'investigationAdmit', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Investigation Details During Admission"
                                                                disabled={!isEditMode}
                                                                rows={4}
                                                            />
                                                        </div>
                                                    </Panel>

                                                    {/* Treatment Given Section */}
                                                    <Panel
                                                        header={
                                                            <Space>
                                                                <MedicineBoxOutlined style={{ fontSize: '18px', color: '#595959' }} />
                                                                <Title level={5} style={{ margin: 0 }}>Treatment Given {values.totalDays ? `(${values.totalDays} Days)` : ''}</Title>
                                                            </Space>
                                                        }
                                                        key="5"
                                                        style={{ marginBottom: '16px', background: 'white', borderRadius: '8px' }}
                                                    >
                                                        <div style={{ padding: '8px 0' }}>
                                                            <CustomTable
                                                                showPagination={false}
                                                                column={columns}
                                                                dataSource={servicesTableData}
                                                                ShowHeader={true}
                                                                Header={
                                                                    <div style={{ marginBottom: '16px' }}>
                                                                        <Title level={5} style={{ margin: 0 }}>Treatment Details</Title>
                                                                    </div>
                                                                }
                                                                key={'treatment-table'}
                                                            />
                                                        </div>
                                                    </Panel>

                                                    {/* Discharge Advice Section */}
                                                    <Panel
                                                        header={
                                                            <Space>
                                                                <HeartOutlined style={{ fontSize: '18px', color: '#595959' }} />
                                                                <Title level={5} style={{ margin: 0 }}>Discharge Advice</Title>
                                                            </Space>
                                                        }
                                                        key="6"
                                                        style={{ marginBottom: '16px', background: 'white', borderRadius: '8px' }}
                                                    >
                                                        <div className="row" style={{ padding: '8px 0' }}>
                                                            <TextAreaField
                                                                label="Investigations Advised"
                                                                name="investigationsAdvised"
                                                                value={values?.investigationsAdvised}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'investigationsAdvised', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Investigations Advised"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Medicine Advised"
                                                                name="medicineAdvised"
                                                                value={values?.medicineAdvised}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'medicineAdvised', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Medicine Advised"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Virechana (Purgation Therapy)"
                                                                name="virechana"
                                                                value={values?.virechana}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'virechana', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Virechana Details"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Exercise"
                                                                name="Exercise"
                                                                value={values?.Exercise}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'Exercise', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Exercise Recommendations"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Diet"
                                                                name="diet"
                                                                value={values?.diet}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'diet', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Diet Recommendations"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Instructions for Mental Peace"
                                                                name="instructionMentalPeace"
                                                                value={values?.instructionMentalPeace}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'instructionMentalPeace', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Instructions for Mental Peace"
                                                                disabled={!isEditMode}
                                                            />

                                                            <TextAreaField
                                                                label="Next Panchakarma Advice"
                                                                name="nextPanchakramaAdvise"
                                                                value={values?.nextPanchakramaAdvise}
                                                                onChange={e => {
                                                                    handleChange({
                                                                        target: { name: 'nextPanchakramaAdvise', value: e.target.value },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                                placeholder="Enter Next Panchakarma Advice"
                                                                disabled={!isEditMode}
                                                            />
                                                        </div>
                                                    </Panel>
                                                </Collapse>

                                                <FormActionButtons
                                                    isEditMode={isEditMode}
                                                    isSubmitting={isSubmitting}
                                                    onToggleEdit={() => {
                                                        toggleEditMode();
                                                    }}
                                                    onPrint={() => handlePrint(values)}
                                                />
                                            </form>
                                        );
                                    }}
                                </Formik>
                            </>
                        )}
                    </Card>
                </div>
            </div>
        </>
    );
};

export default DischargeForm;
